import {
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    withTheme
} from '@material-ui/core';
import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReadonlyEditor from '../../components/drafteditor/components/ReadonlyEditor';
import ImageSlider from '../../components/ImageSlider';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import { AgentCommissionIcon } from '../../icons';
import { formatMoney } from '../../utils';
import moment from 'moment';

function activityLocation(location) {
    const street =
        typeof location?.street !== 'undefined' && location?.street !== ''
            ? `${location?.street},`
            : '';
    const city =
        typeof location?.city !== 'undefined' && location?.city !== '' ? `${location?.city},` : '';
    const state =
        typeof location?.state !== 'undefined' && location?.state !== ''
            ? `${location?.state} -`
            : '';
    const postalCode =
        typeof location?.postalCode !== 'undefined' && location?.postalCode !== ''
            ? `${location?.postalCode}`
            : '';
    const activityLocationValue = `${street} ${city} ${state} ${postalCode}`;
    return activityLocationValue;
}

const OneBlock = ({ col, color = true, children }) => {
    return (
        <Grid item xs={col}>
            <Paper className={`${color ? 'colorcard' : ''} padded`} elevation={0}>
                {children}
            </Paper>
        </Grid>
    );
};

const DividerBlock = () => {
    return (
        <Grid item xs={12}>
            <div style={{ height: 5, backgroundColor: '#f6f6f6' }}></div>
        </Grid>
    );
};

// redirect to SingleActivity
const activityLink = activityId => {
    const savedFilters = window.localStorage.getItem('filters') || '';
    const searchParams = new URLSearchParams(savedFilters);
    searchParams.append('activity', activityId);
    return searchParams.toString();
};

const CompareActivitiesPage = ({ theme, isAgent, isAdmin }) => {
    const { activityId1, activityId2, activityId3 } = useParams();
    const activityIdList =
        typeof activityId3 === 'undefined'
            ? [activityId1, activityId2]
            : [activityId1, activityId2, activityId3];

    const [activitiesData, activitiesLoading] = useSwaggerClient(
        'activities',
        'getAllActivities',
        {
            idList: activityIdList
        },
        [activityIdList]
    );
    const [showCommission, setShowCommission] = useState(false);

    const filteredItems = useMemo(() => {
        const items = activitiesData?.items ?? [];

        if (isAdmin || isAgent) {
            return items;
        }

        return items
            .map(activity => {
                const start = moment(activity?.firstTimeslot?.start_time);

                const tickets = (activity?.tickets ?? []).filter(ticket => {
                    const fromDate = !ticket.date_from || start.isAfter(moment(ticket.date_from));
                    const toDate = !ticket.date_to || start.isBefore(moment(ticket.date_to));
                    return fromDate && toDate;
                });

                return { ...activity, tickets };
            })
            .filter(activity => (activity.tickets?.length ?? 0) > 0);
    }, [activitiesData, isAdmin, isAgent]);

    if (activitiesLoading) {
        return null;
    }

    const aCol = activityIdList.length === 3 ? 3 : 5;
    const tCol = activityIdList.length === 3 ? 3 : 2;

    return (
        <Grid container className="page" spacing={2} style={{ fontSize: '13px' }}>
            <Grid item xs={12}>
                <Link to="/" className="link mb-2" style={{ color: theme.palette.primary.main }}>
                    Back to activities
                </Link>
            </Grid>
            <Grid item xs={tCol}>
                {/* This one is for the title line */}
            </Grid>
            {filteredItems.map((activity, idx) => (
                <Grid item xs={aCol}>
                    <h4>{activity.name}</h4>
                </Grid>
            ))}
            <Grid item xs={tCol}>
                {/* This one is for the imageslider line */}
            </Grid>
            {filteredItems.map((activity, idx) => (
                <Grid item xs={aCol} key={idx}>
                    <ImageSlider images={activity?.extra_info?.images} />
                </Grid>
            ))}
            <Grid item xs={tCol}>
                {/* This one is for the description line */}
            </Grid>
            {filteredItems.map((activity, idx) => (
                <OneBlock col={aCol} key={idx}>
                    <ReadonlyEditor value={activity?.description} />
                </OneBlock>
            ))}
            <DividerBlock />
            <Grid item xs={tCol} container>
                <h4 style={{ alignSelf: 'center' }}>Tickets</h4>
                {(isAgent || isAdmin) && (
                    <Grid item style={{ alignSelf: 'center' }}>
                        <Tooltip title="Commission">
                            <IconButton
                                style={{ opacity: showCommission ? 0.6 : 0.15 }}
                                onClick={() => setShowCommission(!showCommission)}
                            >
                                <AgentCommissionIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
            {filteredItems.map((activity, idx) => (
                <OneBlock col={aCol} key={idx}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ticket name</TableCell>
                                <TableCell style={{ textAlign: 'right' }}>Ticket price</TableCell>
                                {showCommission ? (
                                    <TableCell style={{ textAlign: 'right' }}>
                                        Commission <small>(estimate)</small>
                                    </TableCell>
                                ) : (
                                    ''
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activity?.tickets
                                ?.filter(e => e.retail_price > -1)
                                .map(e => (
                                    <TableRow>
                                        <TableCell>{e.name}</TableCell>
                                        <TableCell style={{ textAlign: 'right' }}>
                                            {formatMoney(e.retail_price)}
                                        </TableCell>
                                        {showCommission ? (
                                            <TableCell style={{ textAlign: 'right' }}>
                                                {formatMoney(e.agentCommission)}
                                            </TableCell>
                                        ) : (
                                            ''
                                        )}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </OneBlock>
            ))}
            <DividerBlock />
            <Grid item xs={tCol}>
                <h4>Additional information</h4>
            </Grid>
            {filteredItems.map((activity, idx) => (
                <OneBlock col={aCol} key={idx}>
                    <ReadonlyEditor value={activity?.extra_info?.info} />
                </OneBlock>
            ))}
            <DividerBlock />
            <Grid item xs={tCol}>
                <h4>Locations</h4>
            </Grid>
            {filteredItems.map((activity, idx) => (
                <OneBlock col={aCol} key={idx}>
                    {activityLocation(activity?.extra_info?.location)}
                </OneBlock>
            ))}
            <DividerBlock />
            <Grid item xs={tCol}>
                <h4>Mandatory requirements</h4>
            </Grid>
            {filteredItems.map((activity, idx) => (
                <OneBlock col={aCol} key={idx}>
                    <ReadonlyEditor value={activity?.booking_notes} />
                </OneBlock>
            ))}
            {isAgent && (
                <>
                    <DividerBlock />
                    <Grid item xs={tCol}>
                        <h4>Vendor notes (for agents)</h4>
                    </Grid>
                    {activitiesData?.items?.map((activity, idx) => (
                        <OneBlock col={aCol} key={idx}>
                            {activity?.vendor_notes}
                        </OneBlock>
                    ))}
                </>
            )}
            <DividerBlock />
            <Grid item xs={tCol}>
                {/* This one is for the booking buttons */}
            </Grid>
            {filteredItems.map((activity, idx) => (
                <Grid item xs={aCol} key={idx} style={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        className="btn-golf-rounded wide-button"
                        color="primary"
                        style={{ color: 'default' }}
                        component={Link}
                        to={`/?${activityLink(activity.id)}`}
                    >
                        Book this activity
                    </Button>
                </Grid>
            ))}
        </Grid>
    );
};

export default withTheme(CompareActivitiesPage);
